import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Checkbox, Divider, Select, Tag } from 'antd';
import SafeImage from 'components/SafeImage';
import { reverse, sortBy, uniq } from 'lodash-es';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';

function ReviewItemSelect({
  data,
  value,
  onChange,
}: {
  data: ReviewAnalysis.Review[];
  value: string[];
  onChange: Dispatch<SetStateAction<string[]>>;
}) {
  const { t } = useTranslate();

  const [searchWord, setSearchWord] = useState('');

  const itemOptions = useMemo(() => {
    return reverse(
      sortBy(
        data
          .reduce<(ReviewAnalysis.Review['itemInfo'] & { count: number })[]>(
            (res, { itemInfo, reviews }) => {
              const target = res.find(({ id }) => id === itemInfo.id);
              if (target) {
                target.count += reviews.length;
              } else {
                res.push({
                  ...itemInfo,
                  count: reviews.length,
                });
              }
              return res;
            },
            [],
          )
          .filter(({ name }) => name.includes(searchWord)),
        'count',
      ),
    );
  }, [data, searchWord]);

  return (
    <Select
      mode="multiple"
      placement="bottomRight"
      dropdownMatchSelectWidth={false}
      placeholder={t('选择商品')}
      value={value}
      onChange={onChange}
      onSearch={setSearchWord}
      tagRender={({ value: current }) => {
        if (current !== value[0]) {
          return <></>;
        }
        return (
          <Tag closable onClose={() => onChange([])}>
            {t('aide.已选x项', { x: value.length })}
          </Tag>
        );
      }}
      dropdownRender={() => (
        <SelectContent>
          <ul>
            <Virtuoso
              data={itemOptions}
              totalCount={itemOptions.length}
              itemContent={(_, item) => {
                const { id, img, name, spuId, itemSku, count } = item;
                const checked = value.includes(id);
                return (
                  <li
                    onClick={() =>
                      onChange(checked ? value.filter((_) => _ !== id) : uniq([...value, id]))
                    }
                  >
                    <Checkbox checked={checked} />
                    <SafeImage width={56} height={56} src={img} />
                    <section>
                      <div>{name}</div>
                      <p>
                        ID：
                        {spuId || '-'}
                        <Divider type="vertical" />
                        {t('主商品货号')}：{itemSku || '-'}
                      </p>
                      <p>{t('共count条评价', { count })}</p>
                    </section>
                  </li>
                );
              }}
            />
          </ul>
        </SelectContent>
      )}
    />
  );
}

const SelectContent = styled.div`
  max-width: 500px;
  min-width: 400px;
  height: 380px;
  overflow: auto;
  > ul {
    height: 100%;
    margin: 0;
    padding: 0;
    list-style: none;
    color: #414141;
    li {
      display: flex;
      align-items: center;
      padding: 12px;
      margin: 12px;
      border: solid 1px #d7dae2;
      cursor: pointer;
      & + li {
        margin-top: 12px;
      }
      > label,
      > div {
        flex: 0 0 auto;
        margin-right: 8px;
      }
      > section {
        flex: 1 0 0;
        overflow: hidden;
        > div {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        > p {
          margin-bottom: 0;
          font-size: 12px;
          color: var(--fett-text-tertiary);
        }
      }
    }
  }
`;

export default ReviewItemSelect;
